import React, {
  useCallback, useEffect, useMemo, useRef, useState
} from 'react';

import classNames from 'classnames';

import { PauseSharp, PlayArrowSharp } from '@mui/icons-material';
import { useSessionContext } from './SessionContext';
import { formatDateString } from './utils';

function DaysPerSecondSlider({ min = 1, max = 30 }) {
  const { daysPerSecond, setDaysPerSecond } = useSessionContext();

  return (
    <div className="frosted slider-container dps">
      <input
        className="slider"
        type="range"
        min={min}
        max={max}
        value={daysPerSecond}
        onChange={(event) => setDaysPerSecond(parseInt(event.target.value, 10))}
      />

    </div>
  );
}

function SpeedButton() {
  const { daysPerSecond } = useSessionContext();
  const [isOpen, setIsOpen] = useState(false);
  const unitLabel = daysPerSecond === 1 ? 'day/sec' : 'days/sec';
  return (
    <div>
      {isOpen && <DaysPerSecondSlider />}
      <button
        type="button"
        className={classNames('speed-control', { open: isOpen })}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="days-per-second-number">{daysPerSecond}</span>
        <span className="days-per-second-label">{unitLabel}</span>
      </button>
    </div>
  );
}

export default function TimelineScrubber() {
  const {
    dates, continuousSelectedDateIndex, setContinuousSelectedDateIndex,
    discreteSelectedDateIndex, daysPerSecond, lightInterface
  } = useSessionContext();
  const [isPlaying, setIsPlaying] = useState(false);
  const [lastTime, setLastTime] = useState();
  const animationRef = useRef(null);

  const max = useMemo(() => (dates ? dates.length - 1 : 0), [dates]);

  const tick = useCallback((timestamp) => {
    if (lastTime) {
      const msPassed = timestamp - lastTime;
      if (msPassed > 30) {
        console.warn(`rendering at <60fps: took ${msPassed}ms to get frame, target is <=17ms`);
      }
      const step = (daysPerSecond / 1000) * msPassed;
      setContinuousSelectedDateIndex((current) => {
        const nextValue = (current + step) % max;
        if (nextValue >= max) return 0;
        return nextValue;
      });
    }
    setLastTime(timestamp);
    animationRef.current = requestAnimationFrame(tick);
  }, [continuousSelectedDateIndex, setContinuousSelectedDateIndex, max, lastTime, daysPerSecond]);

  useEffect(() => {
    if (!isPlaying) {
      setLastTime();
    }
  }, [isPlaying]);

  useEffect(() => {
    if (isPlaying) {
      animationRef.current = requestAnimationFrame(tick);
    }
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, tick]);

  return (
    <div className="frosted timeline-scrubber slider-container">
      {continuousSelectedDateIndex === undefined ? (
        <p style={{ color: 'white' }}>Fetching data...</p>
      ) : (
        <>
          <button
            type="button"
            onClick={() => setIsPlaying(!isPlaying)}
          >
            {isPlaying ? <PauseSharp title="Stop" /> : <PlayArrowSharp title="Animate" />}
          </button>
          <input
            className="slider"
            type="range"
            min={0}
            max={max}
            step={0.1}
            value={continuousSelectedDateIndex}
            onChange={(event) => setContinuousSelectedDateIndex(parseInt(event.target.value, 10))}
          />
          <div className="timeline-date-label">
            <span>{dates ? formatDateString(dates[discreteSelectedDateIndex]) : ''}</span>
          </div>
          {!lightInterface && <SpeedButton />}
        </>
      )}
    </div>
  );
}
